/**
 * Lightweight semantic version comparison utility.
 * Replaces semver library to reduce bundle size.
 *
 * Supports standard semver format: major.minor.patch[-prerelease][+build]
 * Pre-release versions are considered lower than release versions.
 * Build metadata is ignored for comparison purposes.
 */

interface ParsedVersion {
  major: number;
  minor: number;
  patch: number;
  prerelease?: string;
}

/**
 * Parses a semantic version string into its components.
 * @param version - Version string (e.g., "1.2.3", "0.0.2-ga", "1.2.3+build")
 * @returns Parsed version object or null if invalid
 */
function parseVersion(version: string): ParsedVersion | null {
  // Remove build metadata (everything after +)
  const withoutBuild = version.split('+')[0];

  // Split into version and prerelease parts
  const parts = withoutBuild.split('-');
  const versionPart = parts[0];
  const prerelease = parts.length > 1 ? parts.slice(1).join('-') : undefined;

  // Parse major.minor.patch
  const numbers = versionPart.split('.').map(Number);
  if (numbers.length !== 3 || numbers.some(isNaN)) {
    return null;
  }

  return {
    major: numbers[0],
    minor: numbers[1],
    patch: numbers[2],
    prerelease,
  };
}

/**
 * Compares two semantic versions.
 * @param v1 - First version string
 * @param v2 - Second version string
 * @returns -1 if v1 < v2, 0 if v1 === v2, 1 if v1 > v2, or null if either version is invalid
 */
function compareVersions(v1: string, v2: string): number | null {
  const parsed1 = parseVersion(v1);
  const parsed2 = parseVersion(v2);

  if (!parsed1 || !parsed2) {
    return null;
  }

  // Compare major version
  if (parsed1.major !== parsed2.major) {
    return parsed1.major > parsed2.major ? 1 : -1;
  }

  // Compare minor version
  if (parsed1.minor !== parsed2.minor) {
    return parsed1.minor > parsed2.minor ? 1 : -1;
  }

  // Compare patch version
  if (parsed1.patch !== parsed2.patch) {
    return parsed1.patch > parsed2.patch ? 1 : -1;
  }

  // If both have prerelease, compare lexicographically
  // If only one has prerelease, it's considered lower
  if (parsed1.prerelease && parsed2.prerelease) {
    if (parsed1.prerelease === parsed2.prerelease) {
      return 0;
    }
    return parsed1.prerelease > parsed2.prerelease ? 1 : -1;
  }

  if (parsed1.prerelease && !parsed2.prerelease) {
    return -1; // v1 has prerelease, v2 doesn't, so v1 < v2
  }

  if (!parsed1.prerelease && parsed2.prerelease) {
    return 1; // v1 doesn't have prerelease, v2 does, so v1 > v2
  }

  return 0; // Both are release versions with same major.minor.patch
}

/**
 * Checks if version v1 is greater than version v2.
 * @param v1 - First version string
 * @param v2 - Second version string
 * @returns true if v1 > v2, false otherwise (including if either version is invalid)
 */
export function isVersionGreaterThan(v1: string, v2: string): boolean {
  const result = compareVersions(v1, v2);
  return result === 1;
}
